import type { ReactNode } from 'react'
import { forwardRef, useImperativeHandle } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { SearchButton } from '../ListingDrawerSearchControls.styles'
import { HOTEL_DRAWER_MAX_GUESTS } from './constants'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import SearchLocationInput from 'components/Search/SearchForm/SearchLocationInput/SearchLocationInput'
import MobileHotelSearchSummaryBar from 'components/Search/SearchForm/MobileHotelSearchSummaryBar'
import { HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import type { TripStop } from 'tripPlanner/api/types'
import ListingDrawerMobileHotelSearch from '../ListingDrawerMobileHotelSearch'
import ListingDrawerHotelDateGuestsChips from '../ListingDrawerHotelDateGuestsChips'
import useHotelListingSearchControls, { type HotelListingSearchControlsParams } from '../../hooks/useHotelListingSearchControls'

interface Props extends HotelListingSearchControlsParams {
  searchLabel: string
  searchPlaceholder: string
  hotelFilters?: ReactNode
  searchCalendarFocusDate?: Date
  tripStops?: ReadonlyArray<TripStop>
  contextStopId?: number
}

export interface HotelListingSearchControlsHandle {
  openHotelDatePicker: (searchItem?: App.SearchItem) => void
}

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  position: relative;
  width: 100%;
`

const SearchFieldContainer = styled.div`
  flex: 1 1 ${rem(260)};
  width: auto;
`

const SearchField = styled(SearchLocationInput)`
  width: 100%;
`

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
  align-items: stretch;
`

const HotelListingSearchControls = forwardRef<HotelListingSearchControlsHandle, Props>(function HotelListingSearchControls(props, ref) {
  const { searchLabel, searchPlaceholder, searchValue, searchItem, hotelFilters, searchCalendarFocusDate, tripStops, contextStopId, maxGuests = HOTEL_DRAWER_MAX_GUESTS } = props
  const {
    searchState, searchDispatch, controlsContainerRef, searchInputRef, hotelDateGuestsRef,
    isNarrowViewport, isMobileHotelEditorOpen, openMobileHotelEditor, closeMobileHotelEditor,
    committedHotelDateSelection, defaultRooms, summaryDates, summaryGuests, searchButtonLabel,
    recentSearchItems, handleSearchItemChange, handlePhraseChange, handleSubmit,
    commitHotelDateSelection, acceptHotelRooms, discardPendingDateTarget, applySearch,
    submitMobileHotelSearch, openHotelDatePicker,
  } = useHotelListingSearchControls(props)
  useImperativeHandle(ref, () => ({ openHotelDatePicker }), [openHotelDatePicker])

  return (
    <GlobalSearchContextProvider state={searchState} dispatch={searchDispatch}>
      <ControlsContainer ref={controlsContainerRef}>
        {isNarrowViewport ? (
          <MobileHotelSearchSummaryBar label={searchValue} primaryText={summaryDates} secondaryText={summaryGuests} onEdit={openMobileHotelEditor} />
        ) : (
          <Form onSubmit={handleSubmit}>
            <SearchFieldContainer>
              <SearchField
                label={searchLabel}
                placeholder={searchPlaceholder}
                onChange={handleSearchItemChange}
                onPhraseChange={handlePhraseChange}
                variant="dropdown"
                dropdownSize="fill-anchor"
                dropdownPortaled
                inputRef={searchInputRef}
                controlsContainerRef={controlsContainerRef}
                searchVertical="hotel"
                placeTypes={HOTEL_SEARCH_TYPEAHEAD_TYPES}
                recentSearchItems={recentSearchItems}
                showNaturalLanguageSearchSuggestions
              />
            </SearchFieldContainer>

            <SearchButton
              kind="primary"
              size="large"
              type="submit"
              fit="auto"
            >
              {searchButtonLabel}
            </SearchButton>
          </Form>
        )}
        {isNarrowViewport && isMobileHotelEditorOpen && (
          <ListingDrawerMobileHotelSearch searchCalendarFocusDate={searchCalendarFocusDate} searchValue={searchValue} searchItem={searchItem} dates={committedHotelDateSelection} rooms={defaultRooms} maxGuests={maxGuests} onSearch={submitMobileHotelSearch} onClose={closeMobileHotelEditor} />
        )}
      </ControlsContainer>
      <ListingDrawerHotelDateGuestsChips
        ref={hotelDateGuestsRef}
        dates={committedHotelDateSelection}
        rooms={defaultRooms}
        compact={isNarrowViewport}
        showChips={!isNarrowViewport}
        maxGuests={maxGuests}
        focusMonth={searchCalendarFocusDate}
        tripStops={tripStops}
        contextStopId={contextStopId}
        onDatesChange={commitHotelDateSelection}
        onRoomsChange={acceptHotelRooms}
        onCancelDates={discardPendingDateTarget}
        onSearchDates={applySearch}
      >
        {hotelFilters}
      </ListingDrawerHotelDateGuestsChips>
    </GlobalSearchContextProvider>
  )
})

export default HotelListingSearchControls
